import { type FormEvent, type ReactNode, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset, triggerHaptic } from '@repo/shared/client';
import { errorTracker } from '@repo/shared/common';
import { Check } from '~/components/icons';
import {
  Alert,
  Anchor,
  Button,
  Reveal,
  Stack,
  Text,
  TextInput,
  Title,
} from '~/components/ui';

export const Route = createFileRoute('/_auth/forgot-password')({
  component: ForgotPasswordRoute,
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ForgotPasswordRoute(): ReactNode {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function submitAsync(): Promise<void> {
    triggerHaptic();
    setEmailError(undefined);
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError(t('auth.emailInvalid'));
      return;
    }
    setLoading(true);
    try {
      // BetterAuth returns success whether or not the address has an account, so
      // the confirmation is deliberately neutral — it never reveals which emails
      // are registered.
      await requestPasswordReset({ email: email.trim() });
      setSent(true);
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      errorTracker.captureException(wrapped, { context: 'requestPasswordReset' });
      // Still show the neutral confirmation — do not surface send failures to the
      // user (avoids leaking account existence and matches the OTP flow).
      setSent(true);
    } finally {
      setLoading(false);
    }
  }

  const backToSignIn = (
    <Anchor
      component={Link}
      to="/sign-in"
      size="sm"
      data-testid="forgot-password-back-link"
      onClick={() => triggerHaptic()}
    >
      {t('auth.backToSignIn')}
    </Anchor>
  );

  if (sent) {
    return (
      <Reveal variant="fade">
        <Stack gap="xl">
          <div>
            <Title order={1}>{t('auth.forgotPasswordSentTitle')}</Title>
            <Text c="dimmed" size="sm" mt="xs">
              {t('auth.forgotPasswordSentSubtitle')}
            </Text>
          </div>
          <Alert
            icon={<Check size={16} />}
            color="green"
            variant="light"
            data-testid="forgot-password-sent"
          >
            {t('auth.forgotPasswordSentBody', { email: email.trim() })}
          </Alert>
          <Text ta="center" size="sm">
            {backToSignIn}
          </Text>
        </Stack>
      </Reveal>
    );
  }

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void submitAsync();
      }}
      noValidate
    >
      <Reveal variant="fade">
        <Stack gap="xl">
          <div>
            <Title order={1}>{t('auth.forgotPasswordTitle')}</Title>
            <Text c="dimmed" size="sm" mt="xs">
              {t('auth.forgotPasswordSubtitle')}
            </Text>
          </div>

          <TextInput
            label={t('auth.emailLabel')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            error={emailError}
            placeholder={t('auth.emailPlaceholder')}
            autoComplete="email"
            data-testid="forgot-password-email"
          />

          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={loading}
            data-testid="forgot-password-submit"
          >
            {t('auth.forgotPasswordSubmit')}
          </Button>

          <Text ta="center" size="sm">
            {backToSignIn}
          </Text>
        </Stack>
      </Reveal>
    </form>
  );
}
